import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Loader2, User, Mail, Phone, Users, Smartphone } from "lucide-react";
import { PdksActivationDialog } from "@/components/PdksActivationDialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const empty = {
  name: "", email: "", phone: "", role: "operator", department: "", status: "aktif", notes: "",
};

export default function Employees() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can("employees", "edit");  // backend PATCH/DELETE ile birebir
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ roles: [], role_labels: {}, departments: [], department_labels: {} });
  const [loading, setLoading] = useState(true);
  const [pdksEmp, setPdksEmp] = useState(null);
  const [filterDept, setFilterDept] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  // + Yeni Rol / + Yeni Departman mini-dialog
  const [extraOpen, setExtraOpen] = useState(null); // "role" | "department" | null
  const [extraName, setExtraName] = useState("");
  const [extraSaving, setExtraSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [e, m] = await Promise.all([
        api.get("/employees"),
        api.get("/employees/meta"),
      ]);
      setItems(e.data);
      setMeta(m.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((e) => {
      if (filterDept === "all") { /* include all */ }
      else if (filterDept === "__none") { if (e.department) return false; }
      else if ((e.department || "") !== filterDept) return false;
      if (filterStatus !== "all" && e.status !== filterStatus) return false;
      return true;
    });
  }, [items, filterDept, filterStatus]);

  const submitExtra = async () => {
    const name = extraName.trim();
    if (!name) return;
    setExtraSaving(true);
    try {
      const url = extraOpen === "role" ? "/employees/meta/roles" : "/employees/meta/departments";
      const { data } = await api.post(url, { name });
      // Meta'yı yenile (yeni kod + label dahil olsun)
      const { data: m } = await api.get("/employees/meta");
      setMeta(m);
      // Formda diğer bilgiler kaybolmasın — sadece ilgili alanı seç
      if (extraOpen === "role") setForm((f) => ({ ...f, role: data.code }));
      else setForm((f) => ({ ...f, department: data.code }));
      toast.success(data.existing ? "Mevcut kayıt seçildi" : "Eklendi ve seçildi");
      setExtraOpen(null);
      setExtraName("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExtraSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/employees", form);
      toast.success("Çalışan eklendi");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Çalışan PASİF'e alınacak (kayıt ve geçmiş korunur, PDKS mobil erişimi kapanır). Devam?")) return;
    try {
      const { data } = await api.delete(`/employees/${id}`);
      setItems((prev) => prev.map((e) => (e.id === id ? data.employee : e)));
      toast.success("Çalışan pasife alındı");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleStatus = async (emp) => {
    const next = emp.status === "aktif" ? "pasif" : "aktif";
    try {
      const { data } = await api.patch(`/employees/${emp.id}`, { status: next });
      setItems((prev) => prev.map((x) => (x.id === emp.id ? data : x)));
      toast.success(`${data.name} → ${next}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div>
      <PageHeader
        title="Çalışanlar"
        subtitle="Personel + rol + departman haritası"
        breadcrumbs={["Modüller", "Çalışanlar"]}
        actions={
          <Button
            data-testid="add-employee-btn"
            onClick={() => setOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" /> Yeni Çalışan
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex gap-3 flex-wrap">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Departman</Label>
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger className="bg-slate-900 border-slate-700 rounded-sm h-9 w-52"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                <SelectItem value="all">Hepsi</SelectItem>
                <SelectItem value="__none">Departmansız</SelectItem>
                {meta.departments.map((d) => <SelectItem key={d} value={d}>{meta.department_labels[d]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Durum</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="bg-slate-900 border-slate-700 rounded-sm h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                <SelectItem value="all">Hepsi</SelectItem>
                <SelectItem value="aktif">Aktif</SelectItem>
                <SelectItem value="pasif">Pasif</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 px-6">
              <Users className="w-10 h-10 mx-auto text-slate-700 mb-3" />
              <div className="text-slate-400 text-sm">Çalışan bulunmuyor.</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-slate-800">
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ad Soyad</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Rol</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Departman</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">İletişim</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Durum</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((e) => (
                  <TableRow
                    key={e.id}
                    className="border-slate-800 hover:bg-slate-800/40 cursor-pointer"
                    data-testid={`employee-row-${e.id}`}
                    onClick={(ev) => {
                      // Sadece hücreye tıklarsa profil aç — buton/toggle/silme tıklarına dokunma
                      if (ev.target.closest("button, a")) return;
                      navigate(`/calisan/${e.id}`);
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 font-mono text-xs shrink-0">
                          {e.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                        </div>
                        <span className="text-slate-100 font-medium">{e.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-slate-800 text-blue-300 border border-blue-900/60 rounded-sm text-[10px] uppercase tracking-wider">
                        {meta.role_labels[e.role] || e.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm">
                      {e.department ? (meta.department_labels[e.department] || e.department) : <span className="text-slate-600 text-xs">Departmansız</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {e.phone && <div className="text-slate-300 font-mono flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{e.phone}</div>}
                      {e.email && <div className="text-slate-500 flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{e.email}</div>}
                    </TableCell>
                    <TableCell>
                      <button
                        data-testid={`toggle-status-${e.id}`}
                        onClick={() => canEdit && toggleStatus(e)}
                        disabled={!canEdit}
                        title={canEdit ? undefined : "Personel düzenleme yetkiniz yok (employees.edit)"}
                        className={`disabled:cursor-default text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-sm border transition-colors ${
                          e.status === "aktif"
                            ? "text-emerald-300 border-emerald-900 bg-emerald-950/40 hover:bg-emerald-950/60"
                            : "text-slate-500 border-slate-700 bg-slate-800/60 hover:bg-slate-800"
                        }`}
                      >
                        {e.status}
                      </button>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                      {e.status === "aktif" && (
                        <Button
                          data-testid={`pdks-activate-${e.id}`}
                          title="PDKS Aktive Et"
                          variant="ghost"
                          size="sm"
                          onClick={() => setPdksEmp(e)}
                          className="text-slate-400 hover:text-blue-400 hover:bg-blue-950/30 rounded-sm text-xs"
                        >
                          <Smartphone className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">PDKS Aktive Et</span>
                        </Button>
                      )}
                      {canEdit && <Button
                        data-testid={`delete-employee-${e.id}`}
                        title="Pasife Al"
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(e.id)}
                        className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">Yeni Çalışan</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Ad Soyad *</Label>
                <Input
                  data-testid="employee-name-input"
                  required
                  value={form.name}
                  onChange={(ev) => setForm({ ...form, name: ev.target.value })}
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Telefon</Label>
                <Input value={form.phone} onChange={(ev) => setForm({ ...form, phone: ev.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">E-posta</Label>
                <Input type="email" value={form.email} onChange={(ev) => setForm({ ...form, email: ev.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Rol</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => {
                    if (v === "__new__") {
                      setExtraName("");
                      setExtraOpen("role");
                      return;
                    }
                    setForm({ ...form, role: v });
                  }}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm" data-testid="emp-role-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    {meta.roles.map((r) => <SelectItem key={r} value={r}>{meta.role_labels[r]}</SelectItem>)}
                    <SelectItem value="__new__" data-testid="emp-role-new-opt" className="text-cyan-400 font-semibold">+ Yeni Rol</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Departman</Label>
                <Select
                  value={form.department || "none"}
                  onValueChange={(v) => {
                    if (v === "__new__") {
                      setExtraName("");
                      setExtraOpen("department");
                      return;
                    }
                    setForm({ ...form, department: v === "none" ? "" : v });
                  }}
                >
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm" data-testid="emp-dept-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="none">— Departmansız —</SelectItem>
                    {meta.departments.map((d) => <SelectItem key={d} value={d}>{meta.department_labels[d]}</SelectItem>)}
                    <SelectItem value="__new__" data-testid="emp-dept-new-opt" className="text-cyan-400 font-semibold">+ Yeni Departman</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Notlar</Label>
                <Textarea rows={2} value={form.notes} onChange={(ev) => setForm({ ...form, notes: ev.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
              <Button
                data-testid="save-employee-btn"
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* + Yeni Rol / + Yeni Departman mini-modal */}
      <Dialog open={extraOpen !== null} onOpenChange={(v) => !v && setExtraOpen(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" data-testid="emp-extra-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-widest text-xs text-slate-400 font-mono">
              {extraOpen === "role" ? "Yeni Rol" : "Yeni Departman"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Ad</Label>
            <Input
              autoFocus
              data-testid="emp-extra-name"
              value={extraName}
              onChange={(e) => setExtraName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitExtra(); }}
              placeholder={extraOpen === "role" ? "Örn. Bölge Sorumlusu" : "Örn. Ar-Ge"}
              className="bg-slate-950 border-slate-700 rounded-sm"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setExtraOpen(null)}
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
              data-testid="emp-extra-cancel">İptal</Button>
            <Button onClick={submitExtra} disabled={extraSaving || !extraName.trim()}
              className="bg-cyan-600 hover:bg-cyan-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
              data-testid="emp-extra-save">
              {extraSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PdksActivationDialog employee={pdksEmp} onClose={() => setPdksEmp(null)} />
    </div>
  );
}
